// LYTICK Settings Store
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { IndicatorTemplate } from '@/types/indicator'

function sortTimeframes(tfs: string[]): string[] {
  const toMinutes = (tf: string): number => {
    const m = tf.match(/^(\d+)\s*(mn|mo|m|h|d|w)$/i)
    if (m) {
      const num = parseFloat(m[1])
      const unit = m[2].toLowerCase()
      switch (unit) {
        case 'm': return num * 1
        case 'h': return num * 60
        case 'd': return num * 1440
        case 'w': return num * 10080
        case 'mn':
        case 'mo': return num * 43200
        default: return Infinity
      }
    }
    const n = parseFloat(tf)
    return isNaN(n) ? Infinity : n
  }
  return [...tfs].sort((a, b) => toMinutes(a) - toMinutes(b))
}

export interface ChartAppearanceSettings {
  gridVisible: boolean
  crosshairStyle: 'cross' | 'none'
  scaleMode: 'normal' | 'log'
  timezone: string
  candleConvention: 'western' | 'east-asian'
  showWatermark: boolean
}

// ── Custom CSV symbols ─────────────────────────────────────────────────────

export interface CustomCsvSymbol {
  contentHash: string
  symbol: string
  granularitySeconds: number
  firstTs: number
  lastTs: number
  rowCount: number
  addedAt: number
}

// ── Store interface ────────────────────────────────────────────────────────

interface SettingsState {
  defaultSymbol: string
  defaultTimeframe: string
  hapticsEnabled: boolean
  showOHLC: boolean
  soundAlertsEnabled: boolean
  chartType: string
  chartAppearance: ChartAppearanceSettings
  favoriteTimeframes: string[]
  brickSize: number
  rangeSize: number
  indicatorTemplates: IndicatorTemplate[]
  legendCollapsedDesktop: boolean
  recentIndicators: string[]
  paneHeights: Record<string, number>
  autoLoadCsv: boolean
  customCsvSymbols: CustomCsvSymbol[]
  lastActiveCsvHash: string | null

  // Actions
  setDefaultSymbol: (symbol: string) => void
  setDefaultTimeframe: (tf: string) => void
  setHapticsEnabled: (enabled: boolean) => void
  setShowOHLC: (show: boolean) => void
  setSoundAlertsEnabled: (enabled: boolean) => void
  setChartType: (type: string) => void
  setChartAppearance: (settings: Partial<ChartAppearanceSettings>) => void
  setTimezone: (tz: string) => void
  setBrickSize: (size: number) => void
  setRangeSize: (size: number) => void
  addFavoriteTimeframe: (tf: string) => void
  removeFavoriteTimeframe: (tf: string) => void
  toggleFavoriteTimeframe: (tf: string) => void
  addIndicatorTemplate: (template: IndicatorTemplate) => void
  removeIndicatorTemplate: (id: string) => void
  setLegendCollapsedDesktop: (collapsed: boolean) => void
  recordRecentIndicator: (name: string) => void
  setPaneHeight: (id: string, height: number) => void
  setAutoLoadCsv: (value: boolean) => void
  addCustomCsvSymbol: (sym: CustomCsvSymbol) => void
  removeCustomCsvSymbol: (contentHash: string) => void
  setLastActiveCsvHash: (hash: string | null) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      defaultSymbol: '',
      defaultTimeframe: '1h',
      hapticsEnabled: true,
      showOHLC: true,
      soundAlertsEnabled: false,
      chartType: 'candle_solid',
      chartAppearance: {
        gridVisible: true,
        crosshairStyle: 'cross',
        scaleMode: 'normal',
        timezone: typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'UTC',
        candleConvention: 'western',
        showWatermark: true,
      },
      favoriteTimeframes: ['1m', '5m', '15m', '1h', '4h', '1d'],
      brickSize: 10,
      rangeSize: 5,
      indicatorTemplates: [],
      legendCollapsedDesktop: false,
      recentIndicators: [],
      paneHeights: {},
      autoLoadCsv: false,
      customCsvSymbols: [],
      lastActiveCsvHash: null,

      setDefaultSymbol: (symbol) => set({ defaultSymbol: symbol }),
      setDefaultTimeframe: (tf) => set({ defaultTimeframe: tf }),
      setHapticsEnabled: (enabled) => set({ hapticsEnabled: enabled }),
      setShowOHLC: (show) => set({ showOHLC: show }),
      setSoundAlertsEnabled: (enabled) => set({ soundAlertsEnabled: enabled }),
      setChartType: (type) => set({ chartType: type }),
      setChartAppearance: (partial) =>
        set((state) => ({
          chartAppearance: { ...state.chartAppearance, ...partial },
        })),
      setTimezone: (tz: string) =>
        set((state) => ({
          chartAppearance: { ...state.chartAppearance, timezone: tz },
        })),

      setBrickSize: (size) => set({ brickSize: Math.max(0.1, size) }),
      setRangeSize: (size) => set({ rangeSize: Math.max(0.1, size) }),

      addFavoriteTimeframe: (tf) =>
        set((state) => ({
          favoriteTimeframes: sortTimeframes(
            state.favoriteTimeframes.includes(tf)
              ? state.favoriteTimeframes
              : [...state.favoriteTimeframes, tf]
          ),
        })),

      removeFavoriteTimeframe: (tf) =>
        set((state) => ({
          favoriteTimeframes: state.favoriteTimeframes.filter((t) => t !== tf),
        })),

      toggleFavoriteTimeframe: (tf) =>
        set((state) => ({
          favoriteTimeframes: state.favoriteTimeframes.includes(tf)
            ? state.favoriteTimeframes.filter((t) => t !== tf)
            : sortTimeframes([...state.favoriteTimeframes, tf]),
        })),

      addIndicatorTemplate: (template) =>
        set((state) => ({
          indicatorTemplates: [...state.indicatorTemplates, template],
        })),

      removeIndicatorTemplate: (id) =>
        set((state) => ({
          indicatorTemplates: state.indicatorTemplates.filter((t) => t.id !== id),
        })),

      setLegendCollapsedDesktop: (collapsed) => set({ legendCollapsedDesktop: collapsed }),

      recordRecentIndicator: (name) =>
        set((state) => ({
          recentIndicators: [name, ...state.recentIndicators.filter((n) => n !== name)].slice(0, 5),
        })),

      setPaneHeight: (id, height) =>
        set((state) => ({
          paneHeights: { ...state.paneHeights, [id]: height },
        })),

      setAutoLoadCsv: (value) => set({ autoLoadCsv: value }),

      addCustomCsvSymbol: (sym) =>
        set((state) => {
          const idx = state.customCsvSymbols.findIndex((s) => s.contentHash === sym.contentHash)
          if (idx !== -1) {
            const updated = [...state.customCsvSymbols]
            updated[idx] = sym
            return { customCsvSymbols: updated }
          }
          return { customCsvSymbols: [sym, ...state.customCsvSymbols] }
        }),

      removeCustomCsvSymbol: (contentHash) =>
        set((state) => ({
          customCsvSymbols: state.customCsvSymbols.filter((s) => s.contentHash !== contentHash),
          lastActiveCsvHash: state.lastActiveCsvHash === contentHash ? null : state.lastActiveCsvHash,
        })),

      setLastActiveCsvHash: (hash) => set({ lastActiveCsvHash: hash }),
    }),
    {
      name: 'lytick-settings',
      version: 18,
      migrate: (persisted: any, _version: number) => {
        // Drop watchlist (pre-pivot) and mobile/onboarding fields (post desktop-only cut).
        const {
          watchlist: _wl,
          watchlistEntries: _wle,
          recentSymbols: _rs,
          legendCollapsedMobile: _lcm,
          hasSeenOnboarding: _hso,
          ...rest
        } = persisted as any
        return {
          ...rest,
          // Ensure CSV fields exist
          customCsvSymbols: rest.customCsvSymbols ?? [],
          lastActiveCsvHash: rest.lastActiveCsvHash ?? null,
          autoLoadCsv: rest.autoLoadCsv ?? false,
          paneHeights: rest.paneHeights ?? {},
          recentIndicators: rest.recentIndicators ?? [],
          chartAppearance: rest.chartAppearance ?? {
            gridVisible: true,
            crosshairStyle: 'cross',
            scaleMode: 'normal',
            timezone: typeof Intl !== 'undefined'
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : 'UTC',
            candleConvention: 'western',
            showWatermark: true,
          },
          brickSize: rest.brickSize ?? 10,
          rangeSize: rest.rangeSize ?? 5,
          defaultSymbol: '',
          defaultTimeframe: rest.defaultTimeframe ?? '1h',
        }
      },
    }
  )
)
